import { observable, computed, action, makeObservable } from 'mobx'
import WebFont from 'webfontloader'
import { webFonts } from '@client/components/Editor/settings'
import { allFonts } from '@client/webfonts-prepared'

const families = webFonts.map((item) => item.name + ':400,700')

const WebFontConfig = {
  events: true,
  classes: false,
  google: { families },
}

export const loadDefaultFonts = () => {
  WebFont.load(WebFontConfig)
}

export const loadFonts = (families) => {
  const familiesPrepared = families.map((item) => item.name + ':400,700')
  const WebFontConfig = {
    events: true,
    classes: false,
    google: { families: familiesPrepared },
  }
  WebFont.load(WebFontConfig)
}

class FontLoadModel {
  @observable fontNames = []
  @observable fonts = {}

  constructor(fonts = []) {
    makeObservable(this)
    const currentFonts = fonts.filter(
      (item) => allFonts.indexOf(item.name) !== -1
    )
    this.fontNames = currentFonts.map((item) => item.name)
    this.fonts = currentFonts.reduce(
      (result, current) => ({
        ...result,
        [current.name]: {
          ...current,
          variants: {
            n4: {
              loading: false,
              loaded: false,
              error: false,
            },
            n7: {
              loading: false,
              loaded: false,
              error: false,
            },
          },
        },
      }),
      {}
    )
  }

  /**
   * Gets saved values. Local settings override default settings
   * @return {object}
   */
  @computed get allFonts() {
    return fonts
  }

  /**
   * Sets new settings
   * @param {object} newSettings
   */
  @action load = (fonts = []) => {
    const currentFonts = fonts.filter(
      (item) => allFonts.indexOf(item.name) !== -1
    )
    const fontNames = currentFonts.map((item) => item.name)
    this.fontNames = [...new Set(this.fontNames.concat(fontNames))]
    const fontsHash = currentFonts.reduce(
      (result, current) => ({ ...result, [current.name]: current }),
      {}
    )
    fontNames.forEach((name) => {
      if (!this.fonts[name]) {
        this.fonts[name] = {
          ...fontsHash[name],
          variants: {
            n4: {
              loading: false,
              loaded: false,
              error: false,
            },
            n7: {
              loading: false,
              loaded: false,
              error: false,
            },
          },
        }
      }
    })

    const familiesPrepared = fontNames
      .filter((name) => {
        return (
          !this.fonts[name].variants['n4'].loading &&
          !this.fonts[name].variants['n4'].loaded &&
          !this.fonts[name].variants['n4'].error &&
          !this.fonts[name].variants['n7'].loading &&
          !this.fonts[name].variants['n7'].loaded &&
          !this.fonts[name].variants['n7'].error
        )
      })
      .map((name) => name + ':400,700')

    const WebFontConfig = {
      events: true,
      classes: false,
      google: { families: familiesPrepared },
      fontloading: this.fontloading,
      fontactive: this.fontactive,
      fontinactive: this.fontinactive,
    }

    try {
      WebFont.load(WebFontConfig)
    } catch (e) {
      if (e.message !== 'No fonts to load!') {
        throw e
      }
    }
  }

  fontloading = (familyName, fvd) => {
    this.fonts[familyName].variants[fvd] = {
      loading: true,
      loaded: false,
      error: false,
    }
  }

  fontactive = (familyName, fvd) => {
    this.fonts[familyName].variants[fvd] = {
      loading: false,
      loaded: true,
      error: false,
    }
  }

  fontinactive = (familyName, fvd) => {
    this.fonts[familyName].variants[fvd] = {
      loading: false,
      loaded: false,
      error: true,
    }
  }
}

export const NULL = new FontLoadModel([])

export default FontLoadModel
